import { ContentPriority, ContentPrioritySource, TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { ClientContentItemEntity, ClientMonitoringPromptRunEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { faArrowRight, faArrowUp, faQuoteLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useState } from 'react';
import { translateEnumSafe } from '../../../../enum-labels';
import { BrandPresenceFigureCard } from './BrandPresenceFigureCard';
import { HEALTH_TINT_CLASSES } from './HealthScoreCell';
import { resolveHealthCheckScorePresentation } from '../Onboarding/ContentInventory/Components/HealthCheckScorePresentation';
import {
  resolveTaxonomyFacetPresentation,
  resolveTermFromIndex,
  TaxonomyTermNameBadge,
  useTaxonomyTermName,
} from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { useTaxonomyTermIndex } from '../Onboarding/ContentInventory/Components/taxonomyTermIndex';
import { run } from 'holderjs';
import { isDisplayableTerm } from './termStatusPresentation';
import { isSameHealth, toExactScorePercentage, toScorePercentage } from './contentHealth.utils';

type AssignedTermModel = {
  termKey: string;
  facet?: TaxonomyFacet;
};

const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1_000;

function toStableKey(reference: unknown): string | undefined {
  if (!reference) {
    return undefined;
  }

  if (typeof reference === 'string') {
    const key = reference.trim();
    return key.length ? key : undefined;
  }

  if (typeof reference !== 'object') {
    return undefined;
  }

  const candidate = reference as {
    getStableKey?: () => string | undefined;
    stableKey?: string;
    key?: string;
  };

  const methodKey = candidate.getStableKey?.();
  if (typeof methodKey === 'string' && methodKey.trim().length > 0) {
    return methodKey;
  }

  if (typeof candidate.stableKey === 'string' && candidate.stableKey.trim().length > 0) {
    return candidate.stableKey;
  }

  if (typeof candidate.key === 'string' && candidate.key.trim().length > 0) {
    return candidate.key;
  }

  return undefined;
}

/** The page's health as the card's large figure shows it. */
const HEALTH_FIGURE_CLASS_NAME = 'text-[2.5rem] leading-none font-bold tracking-tight text-slate-900 tabular-nums';

/**
 * A change of a page's health between two figures that differ, in points, to one decimal: `+1.8 points`; a change too
 * small for one decimal to its first significant digit, so a change the figures beside it show is never called none.
 */
function toHealthPointsLabel(change: number): string {
  const oneDecimal = Math.round(Math.abs(change) * 10) / 10;
  const points =
    oneDecimal > 0
      ? String(oneDecimal)
      : Math.abs(change).toLocaleString('en-US', { maximumSignificantDigits: 1, maximumFractionDigits: 20 });
  return `${change > 0 ? '+' : '−'}${points} ${Number(points) === 1 ? 'point' : 'points'}`;
}

/** The colours of a change of health: the band colours of a healthy and a critical figure. */
function toHealthChangeClasses(change: number): string {
  return change > 0 ? HEALTH_TINT_CLASSES.Low : HEALTH_TINT_CLASSES.Critical;
}

/** The colours of a health the suggested version keeps. */
const HEALTH_KEPT_CLASSES = 'bg-slate-100 text-slate-600';

/**
 * The page's health now beside its health in the suggested version, each a 0–1 health shown as a whole percentage,
 * with the change between them. A suggested version Sync Core has not measured shows that, never a figure it does
 * not have. A health whose shown figure the suggested version keeps is shown once, "Unchanged" beneath; only a
 * change reads as now and suggested, with the change beneath at its own precision. Where the card is narrower than
 * the two figures, the suggested one moves under the current one rather than past the card's edge.
 */
function HealthComparisonFigure({ current, suggested }: { current?: number; suggested?: number }) {
  const now = toExactScorePercentage(current);
  const then = toExactScorePercentage(suggested);
  const kept = now !== undefined && then !== undefined && isSameHealth({ current, suggested });
  const change = now !== undefined && then !== undefined && !kept ? then - now : undefined;
  const shownNow = toScorePercentage(current);
  const shownThen = toScorePercentage(suggested);

  return (
    <div className="flex min-w-0 max-w-full flex-col items-start">
      {kept ? (
        <span className={HEALTH_FIGURE_CLASS_NAME}>{shownNow}%</span>
      ) : (
        <span className={`flex max-w-full flex-wrap items-baseline gap-x-2 gap-y-1 ${HEALTH_FIGURE_CLASS_NAME}`}>
          <span className="sr-only">Current version: </span>
          <span>{shownNow !== undefined ? `${shownNow}%` : '—'}</span>
          <FontAwesomeIcon icon={faArrowRight} className="text-base text-slate-400" aria-hidden="true" />
          <span className="sr-only">Suggested version: </span>
          <span className={shownThen !== undefined ? undefined : 'text-slate-400'}>{shownThen !== undefined ? `${shownThen}%` : '—'}</span>
        </span>
      )}
      {kept ? (
        <span className={`mt-3 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${HEALTH_KEPT_CLASSES}`}>
          Unchanged
        </span>
      ) : null}
      {change !== undefined ? (
        <span className={`mt-3 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${toHealthChangeClasses(change)}`}>
          {toHealthPointsLabel(change)}
        </span>
      ) : null}
      {suggested === undefined ? (
        <span className="mt-3 inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500">
          Not measured yet
        </span>
      ) : null}
    </div>
  );
}

function toQualityDeltaScaleScore(deltaPercentage: number): number {
  if (!Number.isFinite(deltaPercentage)) {
    return 50;
  }

  if (deltaPercentage >= 0) {
    return 100;
  }

  const shiftedDelta = deltaPercentage - 10;

  if (shiftedDelta <= -20) {
    return 50;
  }

  const normalized = 1 - Math.abs(shiftedDelta) / 20;
  return 50 + normalized * 50;
}

/**
 * The facet an assignment names, as the one value the app compares facets by.
 *
 * What a store answers is text, and the same facet reaches a screen spelled more than one way. Every
 * reader of an assignment goes through here, so two readers of one assignment never disagree about
 * which facet it is — and a card drawing a facet and a card drawing what is left over cannot both
 * take the same tag.
 */
export function toTaxonomyFacet(value: unknown): TaxonomyFacet | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim();
  if (!normalized.length) {
    return undefined;
  }

  const enumValues = Object.values(TaxonomyFacet) as TaxonomyFacet[];
  return enumValues.find((entry) => entry === normalized || entry.toLowerCase() === normalized.toLowerCase());
}

function toAssignedTerms(item: ClientContentItemEntity | undefined): AssignedTermModel[] {
  const assignments = item?.terms?.assigned || [];

  if (!assignments.length) {
    return [];
  }

  const terms = assignments
    .map((assignment): AssignedTermModel | undefined => {
      const termKey = toStableKey(assignment?.term);
      if (!termKey) {
        return undefined;
      }

      const facet = toTaxonomyFacet(assignment?.facet);
      return {
        termKey,
        facet,
      };
    })
    .filter((entry): entry is AssignedTermModel => !!entry);

  const deduped = new Map<string, AssignedTermModel>();
  for (const term of terms) {
    const dedupeKey = `${term.facet || ''}:${term.termKey}`;
    deduped.set(dedupeKey, term);
  }

  return Array.from(deduped.values());
}

function toTermsByFacet(terms: AssignedTermModel[], facet: TaxonomyFacet): AssignedTermModel[] {
  return terms.filter((term) => term.facet === facet);
}

function toAuthorityTerms(terms: AssignedTermModel[]): AssignedTermModel[] {
  return terms.filter(
    (term) => term.facet === TaxonomyFacet.AuthorityTopic || term.facet === TaxonomyFacet.Brand || term.facet === TaxonomyFacet.Product
  );
}

function toTargetAudienceTerms(terms: AssignedTermModel[]): AssignedTermModel[] {
  const facetPriority: TaxonomyFacet[] = [TaxonomyFacet.Persona, TaxonomyFacet.FunnelStage, TaxonomyFacet.Intent];

  return terms
    .filter((term) => term.facet !== undefined && facetPriority.includes(term.facet))
    .sort((left, right) => {
      const leftIndex = facetPriority.indexOf(left.facet as TaxonomyFacet);
      const rightIndex = facetPriority.indexOf(right.facet as TaxonomyFacet);

      if (leftIndex !== rightIndex) {
        return leftIndex - rightIndex;
      }

      return left.termKey.localeCompare(right.termKey);
    });
}

/** The tags of a page that the Page priority card draws as its own rows, one per initiative. */
function toInitiativeTerms(terms: AssignedTermModel[]): AssignedTermModel[] {
  return toTermsByFacet(terms, TaxonomyFacet.Initiative);
}

/** The tags of a page the Page priority card counts, beside the Brand Authority card that names them. */
function toAuthorityTopicTerms(terms: AssignedTermModel[]): AssignedTermModel[] {
  return toTermsByFacet(terms, TaxonomyFacet.AuthorityTopic);
}

/**
 * The facets whose tags these cards already draw by name.
 *
 * It is asked of the selectors the cards render from rather than written out beside them: every facet
 * is run through those selectors once, so a facet added to one of them is a facet this row draws from
 * that moment on, and a screen filling the gap under this row by subtracting this set can never put a
 * tag on itself that a card above it already carries.
 */
export const FACETS_SHOWN_ON_CARDS: ReadonlySet<TaxonomyFacet> = new Set(
  Object.values(TaxonomyFacet).filter((facet) => {
    const probe: AssignedTermModel[] = [{ termKey: String(facet), facet }];

    return !!(
      toAuthorityTerms(probe).length ||
      toTargetAudienceTerms(probe).length ||
      toInitiativeTerms(probe).length ||
      toAuthorityTopicTerms(probe).length
    );
  })
);

/**
 * The grid this row lays its cards out on: two columns on a narrow screen, four on a wide one, in one
 * gutter. A block of cards that continues this row further down the same screen stands on it too, so
 * every card of that screen sits in a column of one grid rather than in a grid of its own.
 */
export const KPI_CARD_ROW_CLASS_NAME = 'row row-cols-2 row-cols-md-4 g-4';

/**
 * The same grid for a screen a CMS frames on its own, such as a page's Optimize tab: one card per row where
 * the frame is phone-narrow, so no card's name wraps, then two, then four, in the same gutter.
 */
export const CMS_KPI_CARD_ROW_CLASS_NAME = 'row row-cols-1 row-cols-sm-2 row-cols-md-4 g-4';

function getAuthorityFacetPriority(facet: TaxonomyFacet | undefined): number {
  if (facet === TaxonomyFacet.Brand) {
    return 0;
  }

  if (facet === TaxonomyFacet.Product) {
    return 1;
  }

  if (facet === TaxonomyFacet.AuthorityTopic) {
    return 2;
  }

  return 3;
}

function isUserAssignedKeyContent(item: ClientContentItemEntity | undefined): boolean {
  if (!item) {
    return false;
  }

  return item.contentPriority === ContentPriority.Critical && item.contentPrioritySource === ContentPrioritySource.Manual;
}

function ImportancePoint(props: { color: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <FontAwesomeIcon icon={faArrowUp} style={{ color: props.color }} className="text-xs" />
      <span>{props.children}</span>
    </div>
  );
}

function InitiativeTextRow({ api, termKey }: { api?: ISyncCoreApi; termKey: string }) {
  // The name comes from the one tag list every badge on the screen reads, as `TaxonomyTermName` reads
  // it, rather than from a read of this one tag.
  const { termName } = useTaxonomyTermName(api, termKey);
  const color = resolveTaxonomyFacetPresentation(TaxonomyFacet.Initiative)?.color || '#64748B';

  return <ImportancePoint color={color}>{termName}</ImportancePoint>;
}

export function ContentItemKpiCards(props: {
  api?: ISyncCoreApi;
  item?: ClientContentItemEntity;
  averageQualityScore?: number;
  /** The grid the cards stand on: the App's page by default, CMS_KPI_CARD_ROW_CLASS_NAME on a CMS screen. */
  rowClassName?: string;
  /**
   * The page's suggested version, while it awaits its review: the Content health card shows the page's health now
   * beside its health in the suggested version, as Sync Core measured it.
   */
  draft?: ClientContentItemEntity;
}) {
  const { api, item, averageQualityScore, rowClassName = KPI_CARD_ROW_CLASS_NAME, draft } = props;

  const qualityScore = toScorePercentage(item?.healthScore);
  const averageQualityPercentage = toScorePercentage(averageQualityScore);
  const qualityDeltaPercentage =
    qualityScore !== undefined && averageQualityPercentage !== undefined ? qualityScore - averageQualityPercentage : undefined;
  const qualityDeltaPresentation =
    qualityDeltaPercentage !== undefined
      ? resolveHealthCheckScorePresentation(toQualityDeltaScaleScore(qualityDeltaPercentage))
      : undefined;
  const qualityDeltaLabel =
    qualityDeltaPercentage !== undefined
      ? `${Math.abs(qualityDeltaPercentage)}% ${qualityDeltaPercentage >= 0 ? 'above' : 'below'} average`
      : '-';

  const importance = translateEnumSafe('ContentPriority', item?.contentPriority as ContentPriority | undefined);
  const { index } = useTaxonomyTermIndex(api);
  // An archived tag is not part of what the customer is looking at, so the badge draws nothing for
  // one. It is dropped here rather than rendered as an empty chip, which is also what lets a card say
  // the page has no tags of its kind when every one of them is archived, as `ContentItemPageFigures`
  // does for the tags under this row.
  const assignedTerms = toAssignedTerms(item).filter((term) => isDisplayableTerm(resolveTermFromIndex(index, term.termKey)?.status));
  const authorityTerms = toAuthorityTerms(assignedTerms);
  const targetAudienceTerms = toTargetAudienceTerms(assignedTerms);
  const authorityTopicTerms = toAuthorityTopicTerms(assignedTerms);
  const initiativeTerms = toInitiativeTerms(assignedTerms);
  const isKeyContent = isUserAssignedKeyContent(item);

  const [monitoringPromptSummary, setMonitoringPromptSummary] = useState<{ citationCount: number; promptCount: number } | undefined>(
    undefined
  );

  useEffect(() => {
    if (!api || !item?.key) {
      setMonitoringPromptSummary(undefined);
      return;
    }

    let active = true;

    void (async () => {
      try {
        const after = Date.now() - THIRTY_DAYS_IN_MS;
        const runs: Array<ClientMonitoringPromptRunEntity> = [];
        let page = 0;
        let numberOfPages = 1;

        do {
          const response = await api.governance.monitoringPromptRuns.list(
            { page, itemsPerPage: 50 },
            {
              contentItemKey: item.key,
              after,
            }
          );

          runs.push(...(response.items || []));
          numberOfPages = Math.max(1, response.numberOfPages || 1);
          page += 1;
        } while (page < numberOfPages);

        const citationCount = runs.length;

        const prompts = new Set<string>(runs.map((run) => run.prompt.getStableKey()));

        if (!active || citationCount <= 0 || prompts.size <= 0) {
          if (active) {
            setMonitoringPromptSummary(undefined);
          }
          return;
        }

        setMonitoringPromptSummary({
          citationCount,
          promptCount: prompts.size,
        });
      } catch {
        if (!active) {
          return;
        }

        setMonitoringPromptSummary(undefined);
      }
    })();

    return () => {
      active = false;
    };
  }, [api, item?.key]);

  // Ordered by the names the badges print, read from the same tag list the badges read them from.
  const sortedAuthorityTerms = useMemo(() => {
    return [...authorityTerms].sort((left, right) => {
      const facetPriorityDiff = getAuthorityFacetPriority(left.facet) - getAuthorityFacetPriority(right.facet);
      if (facetPriorityDiff !== 0) {
        return facetPriorityDiff;
      }

      const leftName = (resolveTermFromIndex(index, left.termKey)?.name || left.termKey).toLowerCase();
      const rightName = (resolveTermFromIndex(index, right.termKey)?.name || right.termKey).toLowerCase();
      return leftName.localeCompare(rightName);
    });
  }, [authorityTerms, index]);

  return (
    <div className={rowClassName}>
      <div className="col">
        <BrandPresenceFigureCard title="Content health">
          {draft ? (
            <HealthComparisonFigure current={item?.healthScore ?? undefined} suggested={draft.healthScore ?? undefined} />
          ) : (
            <div className="flex flex-col items-start">
              <span className={HEALTH_FIGURE_CLASS_NAME}>{qualityScore !== undefined ? `${qualityScore}%` : '—'}</span>
              {/* The band colours come from the shared score presentation, computed per value. */}
              <span
                className="mt-3 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                style={qualityDeltaPresentation ? qualityDeltaPresentation.style : { color: 'rgb(100, 116, 139)' }}
              >
                {qualityDeltaLabel}
              </span>
            </div>
          )}
        </BrandPresenceFigureCard>
      </div>

      <div className="col">
        <BrandPresenceFigureCard title="Page priority">
          <div className="flex flex-col gap-3">
            <span className="text-[1.75rem] leading-none font-bold tracking-tight text-slate-900">{importance}</span>
            <div className="text-xs text-slate-500 space-y-1.5">
              {isKeyContent ? <ImportancePoint color="var(--primary)">Key content</ImportancePoint> : null}
              {authorityTopicTerms.length ? (
                <ImportancePoint color={resolveTaxonomyFacetPresentation(TaxonomyFacet.AuthorityTopic)?.color || '#64748B'}>
                  {authorityTopicTerms.length}x Authority topic
                </ImportancePoint>
              ) : null}
              {initiativeTerms.map((term) => (
                <InitiativeTextRow key={term.termKey} api={api} termKey={term.termKey} />
              ))}
              {monitoringPromptSummary ? (
                <div className="flex items-center gap-2" title="Based on the last 30 days">
                  <FontAwesomeIcon icon={faQuoteLeft} className="text-xs text-slate-500" />
                  <span>
                    Cited {monitoringPromptSummary.citationCount}x across {monitoringPromptSummary.promptCount} prompts
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </BrandPresenceFigureCard>
      </div>

      <div className="col">
        <BrandPresenceFigureCard title="Brand Authority">
          {sortedAuthorityTerms.length ? (
            <div className="flex flex-wrap gap-1.5">
              {sortedAuthorityTerms.map((term) => (
                <TaxonomyTermNameBadge
                  key={`${term.facet || 'unknown'}:${term.termKey}`}
                  api={api}
                  termKey={term.termKey}
                  termFacet={term.facet}
                  applyFacetPresentation
                />
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-500">No authority tags assigned.</div>
          )}
        </BrandPresenceFigureCard>
      </div>

      <div className="col">
        <BrandPresenceFigureCard title="Target Audience">
          {targetAudienceTerms.length ? (
            <div className="flex flex-wrap gap-1.5">
              {targetAudienceTerms.map((term) => (
                <TaxonomyTermNameBadge
                  key={`${term.facet || 'unknown'}:${term.termKey}`}
                  api={api}
                  termKey={term.termKey}
                  termFacet={term.facet}
                  applyFacetPresentation
                />
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-500">No target audience tags assigned.</div>
          )}
        </BrandPresenceFigureCard>
      </div>
    </div>
  );
}
